import Alpine from 'alpinejs';
import { initIframeTitles } from './iframe-titles.js';
import { initFacebookPixel } from './facebook-pixel.js';
import { initAccessibilityButton } from './accessibility-button.js';

// Components

// Global
window.Alpine = Alpine;


// Inits
Alpine.start();
initIframeTitles();
initFacebookPixel();
initAccessibilityButton();

// Reduced-motion state read by the homepage scroll animations and the marquee
// module (both loaded lazily, see below). Seeded from the OS-level preference;
// the accessibility panel's "Reduce motion" toggle (see Accessibility
// Controls) updates this and asks whichever of those modules are loaded to
// refresh themselves.
const reducedMotionState = { enabled: window.matchMedia("(prefers-reduced-motion: reduce)").matches };

// Craft Cookie Consent's own script.js binds its Details/Save/Accept click
// handlers on DOMContentLoaded — but the banner is rendered via
// craft.blitz.includeDynamic() (see _includes/cookie-consent.twig), so its
// HTML only lands later, swapped in by Blitz's own blitzInjectScript.js.
// That script also only *starts* its fetch-and-swap on DOMContentLoaded, so
// the swap can never finish before cookie-consent's synchronous bind() calls
// run on that same, one-time event — bind() always finds nothing, and every
// button in the banner is permanently inert. cookie-consent's listener isn't
// registered with {once: true}, so re-dispatching DOMContentLoaded once the
// swap actually completes makes it re-run its setup against real elements.
document.addEventListener('afterBlitzInject', (e) => {
  if (e.detail && e.detail.element && e.detail.element.querySelector('#elc-cookie-consent-form')) {
    document.dispatchEvent(new Event('DOMContentLoaded', { bubbles: true, cancelable: true }));
  }
});

// Homepage scroll animations (GSAP + ScrollTrigger + InertiaPlugin, see
// homepage-animations.js) are only relevant on pages with these sections, so
// they're loaded as a separate chunk instead of shipping GSAP to every page.
let homepageAnimations = null;
if (document.querySelector('.featured-content, #featured-exhibitions-events, .fs-hero')) {
  import('./homepage-animations.js').then((mod) => {
    homepageAnimations = mod.initHomepageAnimations(reducedMotionState);
  });
}

// Full-screen countdown hero (templates/_blocks/fullScreenCountdown.twig) — homepage
// only, and pulls in Splide, so it's a separate on-demand chunk like the above.
if (document.querySelector('.fs-hero')) {
  import('./fullscreen-hero.js').then((mod) => {
    mod.initFullscreenHero();
  });
}

// Marquees (Splide + AutoScroll, see marquee.js) are likewise only loaded
// when a marquee is actually present — either at initial load, or added
// later via an HTMX/Sprig swap.
let marqueeModule = null;
let marqueeLoadPromise = null;
function ensureMarqueeLoaded() {
  if (marqueeLoadPromise) return marqueeLoadPromise;
  marqueeLoadPromise = import('./marquee.js').then((mod) => {
    marqueeModule = mod;
    // marquee.js self-inits via window.addEventListener('load', ...), which
    // relied on being bundled synchronously so it always registered before
    // 'load' fired. Now that it's fetched on demand, that fetch can easily
    // resolve *after* 'load' already fired, so the listener never runs and
    // the marquee never mounts. Init explicitly here instead; later HTMX/Sprig
    // swaps are still handled by marquee.js's own swap listeners once it's loaded.
    mod.initMarquees();
    return mod;
  });
  return marqueeLoadPromise;
}
if (document.querySelector('[data-marquee]')) {
  ensureMarqueeLoaded();
}
['htmx:afterSwap', 'htmx:oobAfterSwap', 'htmx:afterSettle'].forEach((eventName) => {
  document.addEventListener(eventName, (e) => {
    const root = e.detail?.target || document;
    const hasMarquee = root.matches?.('[data-marquee]') || root.querySelector?.('[data-marquee]');
    if (hasMarquee) {
      ensureMarqueeLoaded();
    }
  });
});

/* Sticky Header */
let lastScroll = window.scrollY;
let scrollAccumulated = 0;
let scrollTimeout;
let lastDirection = null;

const header = document.querySelector('.header-wrapper');
const scrollMenu = document.querySelector('.scroll-menu');
const content = document.querySelector('.home-container');
const navButtons = document.querySelectorAll('.nav-item-btn');
const closeButtons = document.querySelectorAll('.close-btn');
const megaMenus = document.querySelectorAll('.megamenu-panel:not(.mobile-panel)');

// When the homepage's full-screen hero (see fullScreenCountdown.twig) is enabled,
// the full header is a plain in-flow element (position:relative — see the
// `body:has(.fs-hero)` rule in index.css) that renders below the hero and simply
// scrolls away with the page; it's never transformed. The condensed .scroll-menu
// still behaves as usual, but only once you've scrolled past where the full
// header sits — `topZone` is that point (just `50` on every other page).
const hero = document.querySelector('.fs-hero');
let topZone = 50;
function computeTopZone() {
  topZone = hero
    ? Math.max(0, Math.min(hero.offsetHeight, window.innerHeight)) + header.offsetHeight
    : 50;
}
computeTopZone();
window.addEventListener('DOMContentLoaded', computeTopZone);
window.addEventListener('load', computeTopZone);
window.addEventListener('resize', computeTopZone);

// This used to animate header/scrollMenu with gsap.to(), which pulled GSAP
// into every page just for a translateY toggle. It's a plain CSS transition
// now (see .header-wrapper/.scroll-menu in index.css) so GSAP is free to be
// lazy-loaded only where it's actually doing scroll-driven work.
function setHidden(el, hidden) {
  // On the homepage hero the full header stays in normal flow — never move it.
  if (el === header && hero) return;
  el.style.transform = hidden ? 'translateY(-100%)' : 'translateY(0%)';
}

function hideBoth() {
  setHidden(header, true);
  setHidden(scrollMenu, true);
}

function showHeader() {
  setHidden(header, false);
  setHidden(scrollMenu, true);
}

function showScrollMenu() {
  setHidden(header, true);
  setHidden(scrollMenu, false);
}

window.addEventListener('scroll', () => {
  const currentScroll = Math.max(0, window.scrollY);
  const scrollDelta = currentScroll - lastScroll;

  if (scrollDelta === 0) return; // no movement

  const direction = scrollDelta > 0 ? 'down' : 'up';

  // Reset accumulation on direction change
  if (direction !== lastDirection) {
    scrollAccumulated = 0;
    lastDirection = direction;
  }

  scrollAccumulated += scrollDelta;

  // Reset accumulation after 1s pause
  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    scrollAccumulated = 0;
  }, 1000);

  // === Scroll Down ===
  if (direction === 'down' && scrollAccumulated > 50 && currentScroll > topZone) {
    hideBoth();
    scrollAccumulated = 0;
  }

  // === Scroll Up ===
  if (direction === 'up' && Math.abs(scrollAccumulated) > 50) {
    if (currentScroll <= topZone) {
      showHeader();
    } else {
      showScrollMenu();
    }
    scrollAccumulated = 0;
  }

  lastScroll = currentScroll;
});

navButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    // Show the main header, hide the scroll menu
    // (setHidden() no-ops the header itself in homepage hero mode)
    setHidden(header, false);
    setHidden(scrollMenu, true);

    // Reset scrollAccumulated and direction tracking
    scrollAccumulated = 0;
    lastDirection = null;
    lastScroll = window.scrollY;
  });
});

closeButtons.forEach((btn) => {
  btn.addEventListener('click', () => {
    const currentScroll = window.scrollY;

    if (currentScroll > topZone) {
      // Hide the header (no-op in homepage hero mode — the header is in-flow)
      setHidden(header, true);
    }

    // Always hide the scroll-menu
    setHidden(scrollMenu, true);

    // Optional: reset scroll tracking
    scrollAccumulated = 0;
    lastDirection = null;
    lastScroll = currentScroll;
  });
});

function adjustContentOffset() {
  if (!header || !content) return;
  // Only a fixed header is out of flow and needs the content pushed down by its
  // height. An in-flow header (position:relative/sticky — homepage) already takes
  // its own space, so no margin.
  content.style.marginTop =
    getComputedStyle(header).position === 'fixed' ? `${header.offsetHeight}px` : '';
}

// Apply the offset straight away (this module runs after the HTML is parsed and the stylesheet applied) instead of only on
// the window "load" event, which waits for every image: until then the page sat underneath the fixed header. Again once
// the fonts are in (they can change the header's height), and on load/resize as before.
adjustContentOffset();
if (document.fonts && document.fonts.ready) document.fonts.ready.then(adjustContentOffset);
window.addEventListener('load', adjustContentOffset);
window.addEventListener('resize', adjustContentOffset);

// The announcement bar opens ~2s after load (and animates closed when dismissed), which changes the fixed header's
// height after the offset above was measured, so the header slid down over the top of the page. Re-measure on every
// step of that animation so the content moves down with it. Only the bar is observed (not the whole header): opening
// the mobile menu also changes the header's height, and the content should not jump while it is open.
const announcementWrapEl = document.querySelector('.announcement-wrap');
if (announcementWrapEl && 'ResizeObserver' in window) {
  new ResizeObserver(adjustContentOffset).observe(announcementWrapEl);
}

/* Adjust Megamenu height */

function adjustMegaMenuHeight() {
  const headerHeight = header.offsetHeight;
  const viewportHeight = window.innerHeight;
  const minHeight = viewportHeight - headerHeight;

  megaMenus.forEach(panel => {
    panel.style.minHeight = `${minHeight}px`;
    panel.style.maxHeight = `${minHeight}px`;
  });
}
/* Adjust Search height
function adjustSearchHeight() {
  const searchHeaderHeight = document.querySelector('.search-menu').offsetHeight;
  const searchBarHeight = document.querySelector('.search-input-wrapper').offsetHeight;
  const viewportHeight = window.innerHeight;
  const minSearchResultsHeight = viewportHeight - searchHeaderHeight - searchBarHeight;

  document.querySelector('.search-results').style.maxHeight = `${minSearchResultsHeight}px`;
}
*/

// Run on load and resize
window.addEventListener('load', adjustMegaMenuHeight);
window.addEventListener('resize', adjustMegaMenuHeight);

//window.addEventListener('load', adjustSearchHeight);
//window.addEventListener('resize', adjustSearchHeight);

// Accessibility Controls

  document.addEventListener('DOMContentLoaded', () => {
    const html = document.documentElement;

    // Wires a checkbox to an html class + localStorage flag; returns the checkbox (or null).
    // defaultEnabled is only used the first time (no saved preference yet).
    const bindToggle = (id, className, storageKey, defaultEnabled = false) => {
      const checkbox = document.getElementById(id);
      if (!checkbox) return null;

      const stored = localStorage.getItem(storageKey);
      const isEnabled = stored === null ? defaultEnabled : stored === 'true';
      checkbox.checked = isEnabled;
      html.classList.toggle(className, isEnabled);

      checkbox.addEventListener('change', () => {
        localStorage.setItem(storageKey, checkbox.checked);
        html.classList.toggle(className, checkbox.checked);
      });

      return checkbox;
    };

    const highContrastCheckbox = bindToggle('high-contrast', 'high-contrast', 'highContrastEnabled');
    const hideImagesCheckbox = bindToggle('hide-images', 'hide-images', 'hideImagesEnabled');
    // Defaults to the OS-level preference for anyone who hasn't set an explicit choice here.
    const reduceMotionCheckbox = bindToggle('reduce-motion', 'reduce-motion', 'reduceMotionEnabled', reducedMotionState.enabled);
    const underlineLinksCheckbox = bindToggle('underline-links', 'underline-links', 'underlineLinksEnabled');

    // Keep the homepage animations' GSAP matchMedia state (reducedMotionState,
    // read by homepage-animations.js) in sync with this toggle, so GSAP-driven
    // animations respect it too, not just CSS. Both refresh calls are no-ops
    // (via optional chaining) if their module was never loaded on this page.
    if (reduceMotionCheckbox) {
      reducedMotionState.enabled = reduceMotionCheckbox.checked;
      homepageAnimations?.refresh();

      reduceMotionCheckbox.addEventListener('change', () => {
        reducedMotionState.enabled = reduceMotionCheckbox.checked;
        homepageAnimations?.refresh();

        // Marquees are mounted once at page load and only re-check reduced
        // motion at mount time — re-init so a live toggle actually takes
        // effect instead of requiring a reload.
        marqueeModule?.initMarquees();
      });
    }

    /* Text size */
    const FONT_SIZE_STORAGE_KEY = 'fontSizeLevel';
    const FONT_SIZE_CLASSES = { normal: '', large: 'font-size-lg', larger: 'font-size-xl' };
    const fontSizeButtons = document.querySelectorAll('.font-size-btn');

    const applyFontSize = (level) => {
      Object.values(FONT_SIZE_CLASSES).forEach(cls => cls && html.classList.remove(cls));
      if (FONT_SIZE_CLASSES[level]) html.classList.add(FONT_SIZE_CLASSES[level]);
      fontSizeButtons.forEach(btn => {
        btn.setAttribute('aria-pressed', String(btn.dataset.fontSize === level));
      });
    };

    if (fontSizeButtons.length) {
      applyFontSize(localStorage.getItem(FONT_SIZE_STORAGE_KEY) || 'normal');

      fontSizeButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const level = btn.dataset.fontSize;
          localStorage.setItem(FONT_SIZE_STORAGE_KEY, level);
          applyFontSize(level);
        });
      });
    }

    /* Reset to defaults */
    const resetButton = document.getElementById('accessibility-reset');
    if (resetButton) {
      resetButton.addEventListener('click', () => {
        [highContrastCheckbox, hideImagesCheckbox, reduceMotionCheckbox, underlineLinksCheckbox].forEach(checkbox => {
          if (!checkbox || !checkbox.checked) return;
          checkbox.checked = false;
          checkbox.dispatchEvent(new Event('change'));
        });
        localStorage.removeItem(FONT_SIZE_STORAGE_KEY);
        applyFontSize('normal');
      });
    }
  });

  (function () {
  const TOOLTIP_ID = 'megamenu-tooltip';

  // --- Create the tooltip element once ---
  let tooltip = document.getElementById(TOOLTIP_ID);
  if (!tooltip) {
    tooltip = document.createElement('div');
    tooltip.id = TOOLTIP_ID;
    tooltip.setAttribute('role', 'tooltip');
    tooltip.setAttribute('aria-hidden', 'true');
    document.body.appendChild(tooltip);
  }

  // --- Position tooltip near the target element ---
  function positionTooltip(target) {
    const rect = target.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    tooltip.style.left = rect.left + scrollX + 'px';
    tooltip.style.top = (rect.bottom + scrollY + 8) + 'px';
    tooltip.style.maxWidth = Math.min(280, window.innerWidth - 16) + 'px';
  }

  // --- Show ---
  function showTooltip(target, text) {
    tooltip.textContent = text;
    tooltip.setAttribute('aria-hidden', 'false');
    tooltip.classList.add('is-visible');
    positionTooltip(target);
  }

  // --- Hide ---
  function hideTooltip() {
    tooltip.setAttribute('aria-hidden', 'true');
    tooltip.classList.remove('is-visible');
  }

  // --- Wire up all disabled elements inside .megamenu-panel ---
  function initDisabledTooltips() {
    const targets = document.querySelectorAll('.megamenu-panel [data-disabled]');

    targets.forEach(function (el) {
      const text = el.getAttribute('data-disabled');
      if (!text) return;

      el.setAttribute('aria-describedby', TOOLTIP_ID);

      // Mouse
      el.addEventListener('mouseenter', function () { showTooltip(el, text); });
      el.addEventListener('mouseleave', hideTooltip);

      // Touch (tap to toggle on mobile)
      el.addEventListener('touchstart', function () {
        if (tooltip.classList.contains('is-visible') && el.dataset._tooltipOpen) {
          hideTooltip();
          delete el.dataset._tooltipOpen;
        } else {
          showTooltip(el, text);
          el.dataset._tooltipOpen = '1';
        }
      }, { passive: true });

      // Keyboard focus
      el.addEventListener('focus', function () { showTooltip(el, text); });
      el.addEventListener('blur', hideTooltip);
    });

    // Hide on outside tap (mobile)
    document.addEventListener('touchstart', function (e) {
      if (!e.target.hasAttribute('data-disabled')) {
        hideTooltip();
      }
    }, { passive: true });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDisabledTooltips);
  } else {
    initDisabledTooltips();
  }
})();

/***
 * Load Animation
 */

// The page is revealed and the loader dismissed by the inline script in templates/_layouts/base.twig, so neither waits
// for this bundle or for the window "load" event (which only fires once every image and script has downloaded: that
// used to keep the whole page invisible for as long as the slowest request took). The loader's animation is a plain
// animated WebP (web/img/mam-logo-loader-*.webp, made by scripts/render-loader-webp.mjs), so no animation library is
// loaded for it any more.

/**
 * Page transition: fade the page out when following a same-site link. Only after the loader has been shown once this
 * session, as before.
 */
document.addEventListener('click', (e) => {
    // Same as before: the fade-out only applies once the loader has been shown this session.
    if (!sessionStorage.getItem('loaderShown')) return;
    const link = e.target.closest('a');
    if (!link) return;
    if (link.hostname !== window.location.hostname) return;
    if (link.hasAttribute('target')) return;
    // Same-page links (real anchors, or JS-hook placeholders like href="#")
    // aren't real navigation — skip the fade-and-navigate entirely. Checking
    // pathname+search instead of link.hash also covers href="#": browsers
    // resolve its .hash to "" (empty), not "#", so a hash-only check missed it.
    if (link.pathname === window.location.pathname && link.search === window.location.search) return;

    e.preventDefault();
    document.body.classList.remove('is-loaded');

    document.body.addEventListener('transitionend', () => {
        window.location = link.href;
    }, { once: true });
});

// The click handler above fades the body out (removes 'is-loaded', see
// animations.css) *before* navigating away. If the browser restores that
// page from bfcache on Back/Forward, 'pageshow' fires instead of 'load', so
// nothing re-adds 'is-loaded' and the page comes back stuck at opacity: 0 —
// i.e. a white screen.
window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
        document.body.classList.add('is-loaded');
    }
});

document.addEventListener('DOMContentLoaded', () => {
    const normalizePath = (p) => {
        p = (p || '/').split('#')[0].split('?')[0].replace(/\/+$/, '');
        return p === '' ? '/' : p;
    };
    const firstSegment = (p) => {
        const parts = p.split('/').filter(Boolean);
        return parts.length ? parts[0] : '';
    };
    // ancestor-or-self, respecting path segment boundaries
    const isAncestorOrSelf = (ancestor, path) =>
        ancestor === path || path.startsWith(ancestor + '/');

    const currentPath = normalizePath(window.location.pathname);
    const currentFirstSeg = firstSegment(currentPath);

    // A page can be cross-listed under several megamenu sections, but it only
    // has one canonical path. Rather than lighting up every section that links
    // to it, score each section's panel and highlight only the one that truly
    // owns the current URL's hierarchy.
    //
    // Buttons are grouped by menu node id because each section has separate
    // desktop, scroll-bar and mobile triggers pointing at equivalent panels.
    const groups = new Map(); // nodeId -> { buttons, deepest, hasAncestor, segCount }

    document.querySelectorAll('.nav-item-btn[aria-controls]').forEach(el => {
        const controls = el.getAttribute('aria-controls');
        const match = controls && controls.match(/panel-item-(?:mobile-)?(\d+)/);
        if (!match) return;
        const panel = document.getElementById(controls);
        if (!panel) return;

        let group = groups.get(match[1]);
        if (!group) {
            group = { buttons: [], deepest: 0, hasAncestor: false, segCount: 0 };
            groups.set(match[1], group);
        }
        group.buttons.push(el);

        panel.querySelectorAll('a[href]').forEach(link => {
            let linkPath;
            try {
                const url = new URL(link.href, window.location.origin);
                if (url.origin !== window.location.origin) return;
                linkPath = normalizePath(url.pathname);
            } catch (e) {
                return;
            }

            if (currentFirstSeg && firstSegment(linkPath) === currentFirstSeg) {
                group.segCount++;
            }
            if (isAncestorOrSelf(linkPath, currentPath)) {
                if (linkPath.length > group.deepest) group.deepest = linkPath.length;
                if (linkPath !== currentPath) group.hasAncestor = true;
            }
        });
    });

    // Only sections whose panel actually nests the current page (an
    // ancestor-or-self link) are eligible.
    const candidates = [...groups.values()].filter(g => g.deepest > 0);
    if (!candidates.length) return;

    // Prefer the section that contains a real ancestor link, then the deepest
    // match, then the section holding the most links in this URL's first-segment
    // space (the canonical owner). Genuine ties still highlight together.
    candidates.sort((a, b) =>
        (b.hasAncestor - a.hasAncestor) ||
        (b.deepest - a.deepest) ||
        (b.segCount - a.segCount)
    );
    const top = candidates[0];
    candidates.forEach(group => {
        const tie = group === top || (
            group.hasAncestor === top.hasAncestor &&
            group.deepest === top.deepest &&
            group.segCount === top.segCount
        );
        if (tie) {
            group.buttons.forEach(el => el.classList.add('is-active', 'text-orange'));
        }
    });
});

/**
 * Announcement Bar
 */
document.addEventListener('DOMContentLoaded', () => {
    const wrap = document.querySelector('.announcement-wrap');
    const closeBtn = document.getElementById('close-announcement');

    if (!wrap) return;

    // Don't show if dismissed this session
    if (sessionStorage.getItem('announcementDismissed')) {
        wrap.remove();
        return;
    }

    // Collapsed (0 height, hidden) until now, so no gap sits above the header. Opening animates the height.
    setTimeout(() => {
        wrap.classList.add('is-open');
    }, 2000);

    // Close button
    if (closeBtn) {
        closeBtn.addEventListener('click', () => {
            sessionStorage.setItem('announcementDismissed', 'true');
            wrap.classList.remove('is-open');
            setTimeout(() => wrap.remove(), 300);
        });
    }
});

/**
 * Back to Top
 */
document.addEventListener('DOMContentLoaded', () => {
    const backToTop = document.getElementById('back-to-top');
    if (!backToTop) return;

    const SHOW_AFTER_VIEWPORTS = 1.5;
    let ticking = false;

    const updateVisibility = () => {
        const threshold = window.innerHeight * SHOW_AFTER_VIEWPORTS;
        backToTop.classList.toggle('is-visible', window.scrollY > threshold);
        ticking = false;
    };

    window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(updateVisibility);
    }, { passive: true });

    updateVisibility();

    backToTop.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: reducedMotionState.enabled ? 'auto' : 'smooth' });
    });
});
